import { Link } from "react-router-dom";
import {
  BookOpen,
  Calendar,
  ExternalLink,
  Leaf,
  PawPrint,
  Radio,
  ShieldCheck,
  Sparkles,
  Target,
} from "lucide-react";

const PILLARS = [
  {
    icon: BookOpen,
    title: "Stories",
    body: "One new chapter of the Bentley legend published on /bentley on the 1st of every month. Kid-safe, family-shareable, story-driven engagement.",
  },
  {
    icon: Leaf,
    title: "Seasonal Graphics",
    body: "Four fully-illustrated seasonal skins (Spring / Summer / Fall / Winter) refreshing mascot, banners, and camp signage in sync with the calendar.",
  },
  {
    icon: Sparkles,
    title: "Social Content",
    body: "12 platform-ready posts per month for IG / FB / TikTok — captions, hooks, and hashtags tuned to family-camp audiences.",
  },
  {
    icon: Target,
    title: "QR Updates",
    body: "Fresh QR-linked landings for the pavilion, pool, gate, and Bologna Express — rotating monthly so returning guests always find something new.",
  },
  {
    icon: PawPrint,
    title: "Merch Ideas",
    body: "Quarterly capsule drops — tees, stickers, plushies — designed to feel earned, not sold. Bentley as a brand, not a logo.",
  },
  {
    icon: Radio,
    title: "Web Updates",
    body: "Rolling refresh of the standalone /bentley experience: new keyword answers, new sample chips, new hero moments each season.",
  },
  {
    icon: Calendar,
    title: "Holiday Campaigns",
    body: "Themed pushes for the six major camping holidays — Memorial Day, Fourth of July, Labor Day, Halloween Weekend, Thanksgiving, New Year's Eve.",
  },
  {
    icon: ShieldCheck,
    title: "Event Support",
    body: "Dedicated Bentley activation for signature weekends — Hog Roast, Firefly Regatta, Harvest Festival — turning each event into a repeatable IP moment.",
  },
];

export default function Brief() {
  return (
    <div
      className="min-h-screen text-white font-inter"
      style={{ background: "#0A0A0F" }}
      data-testid="brief-page"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 20% 15%, rgba(245,158,11,0.10) 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(34,197,94,0.06) 0%, transparent 55%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(245,158,11,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.6) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage:
              "radial-gradient(ellipse at 50% 30%, black 40%, transparent 85%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at 50% 30%, black 40%, transparent 85%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 pt-8 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <Link
            to="/arsenal"
            data-testid="brief-back-arsenal"
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-slate-400 hover:text-white transition-colors"
          >
            ← Arsenal
          </Link>
          <div
            className="flex items-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.28em]"
            style={{ color: "#F59E0B" }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full light-pulse"
              style={{ backgroundColor: "#F59E0B" }}
            />
            Adventure Club · Brief
          </div>
        </div>

        {/* Hero */}
        <div className="mb-12">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] uppercase tracking-[0.28em] mb-5"
            style={{
              background: "rgba(245,158,11,0.10)",
              border: "1px solid rgba(245,158,11,0.45)",
              color: "#F59E0B",
            }}
            data-testid="brief-eyebrow"
          >
            <PawPrint size={12} />
            Creative Retainer · 1-Page Brief
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-white mb-4"
            data-testid="brief-headline"
          >
            Bentley&apos;s{" "}
            <span style={{ color: "#F59E0B" }}>Adventure Club</span>
          </h1>
          <p
            className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl"
            data-testid="brief-subheadline"
          >
            A monthly creative retainer that turns Bentley from a one-time
            mascot into an evergreen family-camping IP. Every month a fresh
            chapter, every season a new visual coat, every holiday a themed
            campaign — all pushed through the standalone{" "}
            <Link
              to="/bentley"
              className="underline decoration-dotted"
              style={{ color: "#FFE9A8" }}
            >
              /bentley
            </Link>{" "}
            experience.
          </p>
        </div>

        {/* Pillars */}
        <section className="mb-12" data-testid="brief-pillars-section">
          <div className="flex items-center gap-3 mb-6">
            <span
              className="h-px flex-1 max-w-[80px]"
              style={{
                background: "linear-gradient(90deg, rgba(245,158,11,0.5), transparent)",
              }}
            />
            <div className="text-[10px] uppercase tracking-[0.32em] text-slate-400">
              The Eight Pillars
            </div>
            <span
              className="h-px flex-1"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(245,158,11,0.2))",
              }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PILLARS.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="rounded-sm border p-4 transition-colors"
                style={{
                  borderColor: "rgba(245,158,11,0.20)",
                  background:
                    "linear-gradient(180deg, rgba(245,158,11,0.05) 0%, rgba(10,10,15,0.6) 100%)",
                }}
                data-testid={`brief-pillar-${i + 1}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-8 h-8 rounded-sm border flex items-center justify-center"
                    style={{
                      borderColor: "rgba(245,158,11,0.55)",
                      background: "rgba(245,158,11,0.10)",
                      color: "#F59E0B",
                    }}
                  >
                    <Icon size={14} />
                  </div>
                  <div>
                    <div
                      className="text-[9px] uppercase tracking-[0.28em]"
                      style={{ color: "#F59E0B" }}
                    >
                      Pillar {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="text-[14px] font-display tracking-tight text-white">
                      {title}
                    </div>
                  </div>
                </div>
                <p className="text-[13px] text-slate-300 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Deliverable rhythm */}
        <section
          className="mb-12 rounded-sm border p-5"
          style={{
            borderColor: "rgba(255,255,255,0.08)",
            background: "rgba(6,11,20,0.55)",
          }}
          data-testid="brief-cadence"
        >
          <div className="text-[10px] uppercase tracking-[0.32em] text-slate-400 mb-4">
            Delivery Rhythm
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { k: "1st of the month", v: "New Bentley chapter · site refresh" },
              { k: "Every quarter", v: "Seasonal skin + merch capsule" },
              { k: "Event weekend", v: "Themed activation + push campaign" },
            ].map((row) => (
              <div key={row.k}>
                <div className="text-[9px] uppercase tracking-[0.28em] text-[#F59E0B] mb-1">
                  {row.k}
                </div>
                <div className="text-[13px] text-slate-200 leading-relaxed">
                  {row.v}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Positioning */}
        <section
          className="mb-12 rounded-sm border p-6"
          style={{
            borderColor: "rgba(245,158,11,0.35)",
            background:
              "linear-gradient(180deg, rgba(245,158,11,0.08) 0%, rgba(10,10,15,0.7) 100%)",
          }}
          data-testid="brief-positioning"
        >
          <div className="text-[10px] uppercase tracking-[0.32em] text-slate-400 mb-3">
            Why This Matters
          </div>
          <p className="text-base text-slate-200 leading-relaxed">
            A mascot without a plan becomes a t-shirt in the drawer. A mascot
            with an <span className="text-white">Adventure Club</span> becomes
            the reason families come back — and the reason they bring the
            neighbors. Every month, Bentley shows up with something new: a
            story, a stunt, a season, a holiday. Guests forward it. Kids
            memorize it. Google indexes it. And the river stops being a
            campground and becomes a{" "}
            <span style={{ color: "#F59E0B" }}>destination</span>.
          </p>
        </section>

        {/* Links */}
        <section className="mb-12" data-testid="brief-links">
          <div className="text-[10px] uppercase tracking-[0.32em] text-slate-400 mb-4">
            Live Reference Links
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a
              href="/bentley"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brief-link-bentley"
              className="rounded-sm border p-4 flex items-center justify-between transition-colors hover:border-white/30"
              style={{
                borderColor: "rgba(255,233,168,0.30)",
                background: "rgba(255,233,168,0.05)",
              }}
            >
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.28em]"
                  style={{ color: "#FFE9A8" }}
                >
                  Guest Experience
                </div>
                <div className="text-sm text-white font-display">
                  /bentley
                </div>
              </div>
              <ExternalLink size={14} style={{ color: "#FFE9A8" }} />
            </a>
            <a
              href="/demo?name=River+Bend+Family+Campground&city=Caledonia&variant=campground"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brief-link-demo"
              className="rounded-sm border p-4 flex items-center justify-between transition-colors hover:border-white/30"
              style={{
                borderColor: "rgba(34,197,94,0.30)",
                background: "rgba(34,197,94,0.05)",
              }}
            >
              <div>
                <div
                  className="text-[10px] uppercase tracking-[0.28em]"
                  style={{ color: "#22C55E" }}
                >
                  Owner Preview
                </div>
                <div className="text-sm text-white font-display">
                  Sentry Dashboard
                </div>
              </div>
              <ExternalLink size={14} style={{ color: "#22C55E" }} />
            </a>
          </div>
        </section>

        {/* Signoff */}
        <div className="text-center pt-6 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-[0.32em] text-slate-500">
            Prepared for the ones who curate the great outdoors.
          </div>
        </div>
      </div>
    </div>
  );
}
